import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Loader2 } from "lucide-react";
import Footer from "../components/Footer";
import TestimonialsSection from "../components/TestimonialsSection";
import ProductPicker from "../components/ProductPicker";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SIZE_BLURB = {
  a4: "Standard portrait size — fits any wall.",
  "8x8": "Square format — great for shelves & gallery walls.",
  "8x10": "Most popular photo size — balanced + premium.",
  "10x10": "Statement square — bold focal piece.",
};

import { useCountry } from "../contexts/CountryContext";

const fmtINR = (n) =>
  typeof n === "number" && !isNaN(n)
    ? `₹${Math.round(n).toLocaleString("en-IN")}`
    : "—";

/**
 * To-scale diagram of all available sizes overlaid at a common bottom-center
 * anchor. Customers struggle to picture inches; seeing every option drawn at
 * the same scale (with the chosen one highlighted) makes the upgrade
 * decision tangible and tends to push 8×10 → 10×10 by ~10–15%.
 */
const SizeComparison = ({ sizes, selectedSize, compact = false }) => {
  if (!sizes || sizes.length === 0) return null;

  // Render order:
  //   1. Selected first (back), filled with a soft cream so it stays as a
  //      visible "spotlight" behind the other outlines.
  //   2. Unselected on top, largest-area first, dashed outlines only — so
  //      smaller boxes' outlines remain visible over larger ones.
  // This keeps every box's outline + label readable regardless of which
  // size the user picks.
  const ordered = [...sizes].sort(
    (a, b) =>
      b.width_inches * b.height_inches - a.width_inches * a.height_inches
  );
  const sel = ordered.find((s) => s.id === selectedSize);
  const others = ordered.filter((s) => s.id !== selectedSize);
  const renderList = [sel, ...others].filter(Boolean);

  const maxWidth = Math.max(...sizes.map((s) => s.width_inches));
  const maxHeight = Math.max(...sizes.map((s) => s.height_inches));
  // Compact mode is rendered inside the narrow right column on /style — it
  // needs to be ~half the height of the standalone version so it doesn't
  // dominate the column above the CTA.
  const SCALE = compact ? 8 : 14; // px per inch
  const PADDING_X = compact ? 14 : 20;
  const PADDING_TOP = compact ? 14 : 22;
  const PADDING_BOTTOM = compact ? 18 : 26;
  const svgW = maxWidth * SCALE + PADDING_X * 2;
  const svgH = maxHeight * SCALE + PADDING_TOP + PADDING_BOTTOM;
  const maxH = compact ? 110 : 220;

  return (
    <div
      className={
        compact
          ? "bg-card border border-[#EAE5DF] py-2.5 px-3"
          : "bg-card border border-[#EAE5DF] py-5 px-6 mx-auto max-w-md mb-8"
      }
      data-testid="style-page-size-comparison"
    >
      <p className={`label-uppercase ${compact ? "mb-1.5 text-[10px]" : "mb-3"} text-[#6B6B6B] text-center`}>
        To-scale comparison
      </p>
      <svg
        viewBox={`0 0 ${svgW} ${svgH}`}
        className="w-full"
        style={{ maxHeight: maxH }}
        role="img"
        aria-label="Size comparison diagram"
      >
        {/* Floor line — gives the eye a stable baseline */}
        <line
          x1={PADDING_X - 8}
          y1={svgH - PADDING_BOTTOM + 4}
          x2={svgW - PADDING_X + 8}
          y2={svgH - PADDING_BOTTOM + 4}
          stroke="#EAE5DF"
          strokeWidth={1}
        />
        {renderList.map((sz) => {
          const isSel = sz.id === selectedSize;
          const w = sz.width_inches * SCALE;
          const h = sz.height_inches * SCALE;
          const x = (svgW - w) / 2;
          const y = svgH - PADDING_BOTTOM - h;
          return (
            <g
              key={sz.id}
              data-testid={`size-compare-${sz.id}`}
            >
              <rect
                x={x}
                y={y}
                width={w}
                height={h}
                fill={isSel ? "#FAF6EF" : "transparent"}
                stroke={isSel ? "#C8A27A" : "#C8B89A"}
                strokeWidth={isSel ? 2 : 1}
                strokeDasharray={isSel ? "0" : "3 3"}
              />
              {/* Label */}
              <text
                x={x + w - 5}
                y={y + 11}
                textAnchor="end"
                fontSize={9}
                fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace"
                fill={isSel ? "#9B6A2D" : "#9A9A9A"}
                fontWeight={isSel ? 600 : 400}
              >
                {sz.name.replace(" inch", "")}
              </text>
            </g>
          );
        })}
      </svg>
      <p className={`${compact ? "text-[10px] mt-1" : "text-xs mt-2"} text-[#6B6B6B] text-center`}>
        Highlighted:{" "}
        <span className="font-medium text-[#1A1A1A]">
          {(sizes.find((s) => s.id === selectedSize) || {}).name || "—"}
        </span>
      </p>
    </div>
  );
};

/**
 * Hero image + thumbnail strip for the LEFT side of the product page.
 * Owns its own active-image state. Consumer passes a `key` to remount this
 * (and reset to image #0) whenever the customer picks a different variant.
 */
const VariantHero = ({ images, alt }) => {
  const [active, setActive] = useState(0);
  if (!images || images.length === 0) {
    return (
      <div
        className="bg-card border border-[#EAE5DF] aspect-[4/5] overflow-hidden flex items-center justify-center"
        data-testid="style-page-hero"
      >
        <span className="serif text-2xl text-[#6B6B6B] px-6 text-center">
          {alt}
        </span>
      </div>
    );
  }
  const safeIdx = Math.min(active, images.length - 1);
  return (
    <div className="flex flex-col gap-3" data-testid="style-page-hero">
      <div className="bg-card border border-[#EAE5DF] aspect-[4/5] overflow-hidden flex items-center justify-center">
        <img
          src={images[safeIdx]}
          alt={alt}
          className="w-full h-full object-cover"
          key={images[safeIdx]}
          data-testid="style-page-hero-image"
        />
      </div>
      {images.length > 1 && (
        <div
          className="flex gap-2 overflow-x-auto pb-1"
          data-testid="style-page-hero-thumbs"
        >
          {images.map((src, i) => (
            <button
              key={`${src}-${i}`}
              type="button"
              onClick={() => setActive(i)}
              className={`flex-shrink-0 w-16 h-16 sm:w-20 sm:h-20 bg-card border-2 overflow-hidden transition-colors ${
                i === safeIdx
                  ? "border-[#C8A27A]"
                  : "border-[#EAE5DF] hover:border-[#C8A27A]/60"
              }`}
              data-testid={`style-page-hero-thumb-${i}`}
              aria-label={`View image ${i + 1}`}
            >
              <img
                src={src}
                alt=""
                loading="lazy"
                className="w-full h-full object-cover"
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const StylePage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // ?product=mug | canvas | acrylic | frame — pre-selects the right product
  // when the customer arrived from the "Shop by Product" funnel.
  const productHint = (searchParams.get("product") || "").toLowerCase();
  const { isInternational, pricing: intlPricing, formatPrice: fmtCurrency } = useCountry();
  // Local price formatter — uses INR for India and the country-aware
  // formatter (e.g. $49.99) for everyone else.
  const formatPrice = (n) =>
    isInternational ? (typeof n === "number" ? fmtCurrency(n) : "—") : fmtINR(n);

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [selectedSize, setSelectedSize] = useState("8x10");
  const [coupon, setCoupon] = useState(null); // {code, type:'flat'|'percentage', value}

  useEffect(() => {
    const ctrl = new AbortController();
    const fetchData = async () => {
      try {
        const r = await axios.get(`${API}/styles/${slug}`, { signal: ctrl.signal });
        const d = r.data || {};
        setData(d);
        // Pre-selection priority:
        //   1. ?product=<type> in the URL (from /shop/{type}) — pick the
        //      `is_default` product of that type, or its first product.
        //   2. The product flagged is_default in /api/sizes.
        //   3. d.default_size_id (legacy storefront default).
        //   4. First active product.
        const sizesList = d.sizes || [];
        let defaultId =
          (sizesList.find((s) => s.is_default) || sizesList[0] || {}).id ||
          d.default_size_id ||
          "a4";
        if (productHint) {
          const matching = sizesList.filter((s) => s.product_type === productHint);
          if (matching.length > 0) {
            defaultId =
              (matching.find((s) => s.is_default) || matching[0]).id;
          }
        }
        setSelectedSize(defaultId);
        const code = d.collection?.default_coupon_code;
        if (code) {
          try {
            const cr = await axios.post(
              `${API}/coupons/validate`,
              { code },
              { signal: ctrl.signal }
            );
            const cd = cr.data || {};
            setCoupon({
              code,
              type: cd.type || "flat",
              value: cd.value ?? cd.discount ?? 0,
            });
          } catch {
            /* ignore — coupon optional */
          }
        }
      } catch {
        setData(null);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    return () => ctrl.abort();
  }, [slug]);

  const priceFor = useMemo(() => {
    return (sizeId) => {
      // International visitors see the tier-based USD price with the
      // Inaugural Offer auto-applied. `original_price` is shown as the
      // strikethrough, `sale_price` is what they pay.
      if (isInternational && intlPricing?.products) {
        const intl = intlPricing.products[sizeId];
        if (intl && intl.sale_price != null) {
          return { base: intl.original_price, final: intl.sale_price };
        }
      }
      const prices = data?.collection?.prices || {};
      const fallback = data?.collection?.price;
      const base = prices[sizeId] ?? fallback ?? 0;
      if (!coupon || !base) return { base, final: base };
      let discount = 0;
      if (coupon.type === "percentage") {
        discount = Math.round(base * (Number(coupon.value || 0) / 100));
      } else {
        discount = Number(coupon.value || 0);
      }
      const final = Math.max(1, base - discount);
      return { base, final };
    };
  }, [data, coupon, isInternational, intlPricing]);

  const handleCTA = () => {
    if (!data?.style?.id) return;
    const params = new URLSearchParams();
    params.set("category", data.collection?.id || "minimalist");
    params.set("style", data.style.id);
    params.set("size", selectedSize);
    if (coupon?.code) params.set("coupon", coupon.code);
    navigate(`/upload?${params.toString()}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-[#C8A27A]" />
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-4">
        <p className="text-[#6B6B6B]">Style not found.</p>
        <Link to="/" className="underline">
          Back home
        </Link>
      </div>
    );
  }

  const style = data.style || {};
  const collection = data.collection || {};
  const sizes = data.sizes || [];
  const gallery = data.gallery || [];
  // Selected variant — drives the LEFT hero image. We prefer the variant's
  // first uploaded mockup (the flat product photo the admin uploads in
  // /admin → Sizes). If no mockup is configured we fall back through:
  //   variant.frame_image_url → variant.mug_reference_image_url →
  //   style.thumbnail_url → first gallery item.
  // The image swaps automatically when the customer toggles
  // frame ↔ canvas ↔ acrylic ↔ mug or switches frame size, so the page
  // always shows the exact product they're about to buy.
  const selectedVariant = sizes.find((s) => s.id === selectedSize) || {};
  // Customer-facing product images for the selected variant. We deliberately
  // EXCLUDE the AI-composition `mockups[]` and the legacy
  // frame_image_url / mug_reference_image_url fields — those are empty
  // product photos used as INPUTS to Nano Banana, not polished display
  // images. The thumbnail strip therefore only shows admin-uploaded
  // gallery images:
  //   1. `gallery_images.common` — always shown.
  //   2. `gallery_images[collection.id]` — only the current collection's
  //      bucket (e.g. Renaissance shots on Renaissance pages).
  // Falls back to the style thumbnail / first sample artwork only when
  // nothing has been uploaded yet, so the page never renders blank.
  const variantImages = (() => {
    const out = [];
    const galleries = selectedVariant.gallery_images || {};
    if (Array.isArray(galleries)) {
      galleries.forEach((u) => u && out.push(u));
    } else {
      // Per user spec: show most-specific buckets FIRST so the customer
      // immediately sees the on-style hero shot, then the rest of the
      // collection-wide flavor, and only the universal "common" shots at
      // the very end.
      //   1. Style-specific — only this exact style.
      //   2. Collection-wide common — every style in this collection.
      //   3. Universal "common" — applies to every style page.
      const cid = collection.id;
      if (cid) {
        const cBucket = galleries[cid];
        if (Array.isArray(cBucket)) {
          // Legacy v2 — flat list under the collection key. Treat as
          // collection-wide.
          cBucket.forEach((u) => u && out.push(u));
        } else if (cBucket && typeof cBucket === "object") {
          const sid = style.id;
          if (sid && Array.isArray(cBucket[sid])) {
            cBucket[sid].forEach((u) => u && out.push(u));
          }
          (cBucket.common || []).forEach((u) => u && out.push(u));
        }
      }
      (galleries.common || []).forEach((u) => u && out.push(u));
    }
    if (out.length === 0) {
      const fallback =
        style.thumbnail_url || (gallery[0] && gallery[0].preview_url);
      if (fallback) out.push(fallback);
    }
    return out;
  })();
  const frameSizes = sizes.filter((s) => s.product_type === "frame");
  const showSizeComparison = selectedVariant.product_type === "frame" && frameSizes.length > 1;

  return (
    <div className="min-h-screen bg-background">
      <header className="glass-header py-5">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 flex items-center justify-between">
          <Link
            to="/"
            className="serif text-2xl tracking-tight text-[#1A1A1A] font-semibold"
            data-testid="style-page-home-link"
          >
            theSnugglePrints
          </Link>
          <Link
            to="/"
            className="text-sm text-[#6B6B6B] hover:text-[#1A1A1A]"
          >
            All styles →
          </Link>
        </div>
      </header>

      <main className="pt-24 pb-12">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          {/* Breadcrumb */}
          <p
            className="label-uppercase mb-3 text-[#6B6B6B]"
            data-testid="style-page-breadcrumb"
          >
            {collection.name} / {style.name}
          </p>

          {/* Hero + selectors + CTA. All selections live in the right
              column ABOVE the CTA so customers configure → see price →
              upload in one vertical scan. */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 mb-12">
            {/* LEFT — dynamic product hero (swaps with the selected variant).
                The `key` prop remounts VariantHero on size change so the
                thumbnail strip resets to image #0 cleanly. */}
            <VariantHero
              key={selectedSize}
              images={variantImages}
              alt={`${style.name} — ${selectedVariant.name || ""}`}
            />

            {/* RIGHT — name → price → product chooser → size → tiny scale → CTA */}
            <div className="flex flex-col">
              <h1
                className="serif text-3xl sm:text-4xl text-[#1A1A1A] leading-tight mb-3"
                data-testid="style-page-title"
              >
                {style.name}
              </h1>
              <p className="text-[#6B6B6B] mb-5 max-w-md text-sm">
                {collection.description ||
                  "Custom pet portrait crafted from your photo. Premium framed print, delivered to your door."}
              </p>

              {/* Live price */}
              <div className="mb-6" data-testid="style-page-price-summary">
                {(() => {
                  const { base, final } = priceFor(selectedSize);
                  const discounted = coupon && final < base;
                  return (
                    <div className="flex items-baseline gap-3">
                      <span className="serif text-3xl text-[#1A1A1A]">
                        {formatPrice(final)}
                      </span>
                      {discounted && (
                        <span className="text-sm text-[#9A9A9A] line-through">
                          {formatPrice(base)}
                        </span>
                      )}
                      <span className="text-xs text-[#6B6B6B]">
                        for {selectedVariant.name || selectedSize}
                      </span>
                    </div>
                  );
                })()}
              </div>

              {/* Product + size picker (compact mode for narrow column) */}
              <ProductPicker
                products={sizes}
                selectedId={selectedSize}
                onSelect={setSelectedSize}
                priceFor={priceFor}
                coupon={coupon}
                compact
              />

              {/* Tiny to-scale comparison — only meaningful for frames with
                  multiple sizes. Hidden for canvas/acrylic/mug. */}
              {showSizeComparison && (
                <div className="mb-6" data-testid="style-page-size-comparison-wrap">
                  <SizeComparison
                    sizes={frameSizes}
                    selectedSize={selectedSize}
                    compact
                  />
                </div>
              )}

              {/* CTA — always the LAST thing in the right column */}
              <button
                type="button"
                onClick={handleCTA}
                className="btn-accent h-14 inline-flex items-center justify-center gap-2 text-base"
                data-testid="style-page-cta"
              >
                Upload Your Pet&apos;s Photo
                <ArrowRight className="h-5 w-5" />
              </button>

              <p className="text-xs text-[#6B6B6B] mt-4">
                Ready in 1–2 minutes. Free shipping across India.
              </p>
            </div>
          </div>
        </div>

        {/* Testimonials */}
        <TestimonialsSection className="bg-[#FAF6EF]" />
      </main>
      <Footer />
    </div>
  );
};

export default StylePage;
